import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../arca_Auth/AuthContext';
import { Building, Users as UsersIcon, Heart, Handshake, X, Menu, ChevronDown } from 'lucide-react';
import arcaLogo from '../../assets/arca_logo.png';

const PrimaryNavbar = ({ user: propUser, onLogout }) => {
  const { user: contextUser } = useAuth();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdown, setMobileDropdown] = useState(null);

  // Use prop user if provided (from dashboard), otherwise use context user
  const user = propUser || contextUser;

  const handleMouseEnter = (menu) => {
    setActiveDropdown(menu);
  };

  const handleMouseLeave = () => {
    setActiveDropdown(null);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    if (!mobileMenuOpen) {
      setMobileDropdown(null);
    }
  };

  const toggleMobileDropdown = (menu) => {
    setMobileDropdown(mobileDropdown === menu ? null : menu);
  };

  // More robust check for user authentication
  const isUserLoggedIn = user && Object.keys(user).length > 0;

  // Mobile menu items
  const mobileMenuItems = [
    { title: 'Home', link: '/', icon: Building },
    {
      title: 'About Us',
      icon: Building,
      items: [
        { title: 'Our Story', link: '/about', icon: Building, description: 'Learn about ARCA\'s journey' },
        { title: 'Mission, Vision & Values', link: '/about/mission-vision', icon: Heart, description: 'Our guiding principles' },
        { title: 'Leadership Team', link: '/about/leadership', icon: UsersIcon, description: 'Meet our leaders' },
        { title: 'Partners & Collaborators', link: '/about/partners', icon: Handshake, description: 'Our trusted partners' }
      ]
    },
    { title: 'Events', link: '/events', icon: UsersIcon },
    { title: 'ARCA Store', link: '/store', icon: Building },
    { title: 'Contact Us', link: '/contact', icon: Heart }
  ];

  return (
    <nav className="navbar navbar-expand-lg navbar-light primary-navbar border-bottom" style={{ minHeight: '60px' }}>
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center w-100">
          {/* Logo */}
          <Link to="/" className="navbar-brand d-flex align-items-center me-4">
            <img src={arcaLogo} alt="ARCA Logo" style={{ height: '50px', width: 'auto' }} className="me-2" />
            <div className="d-flex flex-column">
              <span style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: '#FFFFFF',
                lineHeight: '1.2'
              }}>ARCA</span>
              <span className="d-none d-xl-block" style={{ fontSize: '0.7rem', color: '#A0A5A5', lineHeight: '1.1' }}>
                Africa Respiratory Care Association
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="d-none d-lg-flex align-items-center">
            {/* Home */}
            <Link
              to="/"
              className="nav-link text-white me-3 px-3 py-2 rounded"
              style={{ fontSize: '1rem', textDecoration: 'none', fontWeight: '500' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              Home
            </Link>

            {/* About Us - Mega Menu Dropdown */}
            <div
              className="dropdown me-3"
              onMouseEnter={() => handleMouseEnter('about')}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className="btn btn-link text-white px-3 py-2 rounded d-flex align-items-center"
                style={{ fontSize: '1rem', textDecoration: 'none', border: 'none', fontWeight: '500' }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                About Us
                <span className="ms-1" style={{ fontSize: '0.8rem' }}>▼</span>
              </button>

              {activeDropdown === 'about' && (
                <div className="dropdown-menu mega-menu show position-absolute shadow-lg border-0" style={{ top: '100%', left: '0', zIndex: 1050, minWidth: '500px', borderRadius: '8px' }}>
                  <div className="container p-4">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <Link to="/about" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <Building className="me-3 mt-1 flex-shrink-0" style={{ color: '#6B8E23' }} size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Our Story</div>
                              <small className="text-muted">Learn about ARCA's journey</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                      <div className="col-md-6">
                        <Link to="/about/mission-vision" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <Heart className="me-3 mt-1 flex-shrink-0" style={{ color: '#6B8E23' }} size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Mission, Vision & Values</div>
                              <small className="text-muted">Our guiding principles</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                      <div className="col-md-6">
                        <Link to="/about/leadership" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <UsersIcon className="me-3 mt-1 flex-shrink-0" style={{ color: '#6B8E23' }} size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Leadership Team</div>
                              <small className="text-muted">Meet our leaders</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                      <div className="col-md-6">
                        <Link to="/about/partners" className="dropdown-item d-flex align-items-start p-3 rounded" style={{ transition: 'all 0.2s', textDecoration: 'none' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div className="d-flex align-items-start">
                            <Handshake className="me-3 mt-1 flex-shrink-0" style={{ color: '#6B8E23' }} size={20} />
                            <div>
                              <div className="fw-bold text-dark mb-1">Partners & Collaborators</div>
                              <small className="text-muted">Our trusted partners</small>
                            </div>
                          </div>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Events */}
            <Link
              to="/events"
              className="nav-link text-white me-3 px-3 py-2 rounded"
              style={{ fontSize: '1rem', textDecoration: 'none', fontWeight: '500' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              Events
            </Link>

            {/* ARCA Store */}
            <Link
              to="/store"
              className="nav-link text-white me-3 px-3 py-2 rounded"
              style={{ fontSize: '1rem', textDecoration: 'none', fontWeight: '500' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              ARCA Store
            </Link>

            {/* Contact Us */}
            <Link
              to="/contact"
              className="nav-link text-white me-3 px-3 py-2 rounded"
              style={{ fontSize: '1rem', textDecoration: 'none', fontWeight: '500' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              Contact Us
            </Link>
          </div>

          {/* Desktop Right Side Navigation */}
          <div className="d-none d-lg-flex align-items-center">
            {/* Be a Member */}
            <Link
              to="/membership_registration"
              className="btn btn-success me-2 fw-bold"
              style={{ fontSize: '0.938rem' }}
            >
              Be a Member
            </Link>

            {/* Become a Vendor */}
            <Link
              to="/get-involved/vendor-membership"
              className="btn me-2 fw-bold text-white"
              style={{ fontSize: '0.938rem', backgroundColor: '#556B2F' }}
            >
              Become a Vendor
            </Link>

            {/* Login, Dashboard or Logout */}
            {isUserLoggedIn ? (
              <>
                <Link
                  to="/my-dashboard"
                  className="btn btn-primary fw-bold me-2"
                  style={{ fontSize: '0.938rem' }}
                >
                  My Dashboard
                </Link>
                {onLogout && (
                  <button
                    onClick={onLogout}
                    className="btn btn-danger fw-bold"
                    style={{ fontSize: '0.938rem' }}
                  >
                    Logout
                  </button>
                )}
              </>
            ) : (
              <Link
                to="/login"
                className="btn btn-primary fw-bold"
                style={{ fontSize: '0.938rem' }}
              >
                Login
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="navbar-toggler d-lg-none"
            type="button"
            onClick={toggleMobileMenu}
            style={{ border: 'none', background: 'transparent' }}
          >
            {mobileMenuOpen ? <X size={24} color="white" /> : <Menu size={24} color="white" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="d-lg-none bg-dark position-fixed w-100 h-100" style={{ zIndex: 9999, overflowY: 'auto', top: 0, left: 0 }}>
          <div className="container py-3">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center">
                <img src={arcaLogo} alt="ARCA Logo" style={{ height: '40px', width: 'auto' }} className="me-2" />
                <span className="text-white fw-bold">ARCA</span>
              </div>
              <button
                className="btn btn-link p-0"
                onClick={toggleMobileMenu}
                style={{ border: 'none', background: 'transparent' }}
                aria-label="Close menu"
              >
                <X size={28} color="white" />
              </button>
            </div>

            <div className="d-flex flex-column">
              {mobileMenuItems.map((item, index) => (
                <div key={index} className="mb-2">
                  {item.items ? (
                    <>
                      <button
                        className="btn btn-dark text-white w-100 d-flex justify-content-between align-items-center py-3"
                        onClick={() => toggleMobileDropdown(item.title)}
                        style={{ border: 'none', borderRadius: '0', textAlign: 'left' }}
                      >
                        <div className="d-flex align-items-center">
                          <item.icon size={20} className="me-3" />
                          {item.title}
                        </div>
                        <ChevronDown
                          size={20}
                          style={{ transform: mobileDropdown === item.title ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.3s' }}
                        />
                      </button>

                      {mobileDropdown === item.title && (
                        <div className="bg-secondary bg-opacity-25">
                          {item.items.map((subItem, subIndex) => (
                            <Link
                              key={subIndex}
                              to={subItem.link}
                              className="d-flex text-white text-decoration-none py-3 px-4 border-bottom border-dark"
                              onClick={toggleMobileMenu}
                            >
                              <subItem.icon size={20} className="me-3 flex-shrink-0" />
                              <div>
                                <div className="fw-bold">{subItem.title}</div>
                                <small className="text-white-50">{subItem.description}</small>
                              </div>
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.link}
                      className="btn btn-dark text-white w-100 d-flex align-items-center py-3"
                      style={{ border: 'none', borderRadius: '0', textAlign: 'left' }}
                      onClick={toggleMobileMenu}
                    >
                      <item.icon size={20} className="me-3" />
                      {item.title}
                    </Link>
                  )}
                </div>
              ))}

              {/* Mobile Auth Buttons */}
              <div className="mt-4">
                <Link
                  to="/membership_registration"
                  className="btn btn-success w-100 mb-3 fw-bold"
                  onClick={toggleMobileMenu}
                >
                  Be a Member
                </Link>

                {/* Become a Vendor */}
                <Link
                  to="/get-involved/vendor-membership"
                  className="btn w-100 mb-3 fw-bold text-white"
                  style={{ backgroundColor: '#556B2F' }}
                  onClick={toggleMobileMenu}
                >
                  Become a Vendor
                </Link>

                {isUserLoggedIn ? (
                  <>
                    <Link
                      to="/my-dashboard"
                      className="btn btn-primary w-100 mb-3 fw-bold"
                      onClick={toggleMobileMenu}
                    >
                      My Dashboard
                    </Link>
                    {onLogout && (
                      <button
                        onClick={() => {
                          onLogout();
                          toggleMobileMenu();
                        }}
                        className="btn btn-danger w-100 fw-bold"
                      >
                        Logout
                      </button>
                    )}
                  </>
                ) : (
                  <Link
                    to="/login"
                    className="btn btn-primary w-100 fw-bold"
                    onClick={toggleMobileMenu}
                  >
                    Login
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default PrimaryNavbar;